import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, ActionCreator, Creator, Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';
import { SessionServiceFactory } from '@arc/unified-payments-frontend/shared-payment';
import {
  selectExternalUserUrn,
  selectSessionId,
  selectVirtualOtherwisePrimaryTenantId,
} from '../session-store';
import { selectUserEmail } from '../external-selectors';

/**
 * Factory that creates a reusable NgRx functional effect for updating session
 * ownership after a payment preview update succeeds.
 *
 * The effect listens for a configurable trigger action (typically
 * paymentPreviewUpdated), reads tenant ID, session ID, and external user URN
 * from the store, calls
 * PUT /v1/tenants/{tenantId}/paymentsessions/{sessionId}/paymentlinks/ownership,
 * then dispatches either the provided success or error action.
 *
 * Usage (POI, POA, international — each passes its own action set):
 *
 *   export const poiUpdateSessionOwnershipEffect = createUpdateSessionOwnershipEffect({
 *     triggerAction: MakePaymentPaymentInformationApiActions.paymentPreviewUpdated,
 *     successAction: MakePaymentSessionApiActions.sessionOwnershipUpdated,
 *     errorAction: MakePaymentSessionApiActions.sessionOwnershipUpdateError,
 *   });
 */
export function createUpdateSessionOwnershipEffect<
  A extends Action<string>,
>(config: {
  triggerAction: ActionCreator<string, Creator<any[], A>>;
  successAction: ActionCreator;
  errorAction: ActionCreator;
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      sessionServiceFactory = inject(SessionServiceFactory),
    ) =>
      actions$.pipe(
        ofType(config.triggerAction),
        concatLatestFrom(() => [
          store.select(selectVirtualOtherwisePrimaryTenantId),
          store.select(selectSessionId),
          store.select(selectExternalUserUrn),
          store.select(selectUserEmail),
        ]),
        exhaustMap(([, tenantId, sessionId, externalUserUrn, userEmail]) =>
          sessionServiceFactory
            .create()
            .updateSessionOwnershipV1(tenantId, sessionId, {
              externalUserUrn: externalUserUrn,
            })
            .pipe(
              map(() => config.successAction({ userEmail }) as Action),
              catchError((error: HttpErrorResponse) =>
                of(config.errorAction({ error }) as Action),
              ),
            ),
        ),
      ),
    { functional: true },
  );
}
